import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import introductionService from '../services/introductionService';
import { searchUsers, UserSearchResult } from '../services/userSearchService';
import UserSearchBar from './UserSearchBar';
import { Icon } from './icons';
import { X, User, Send, CheckCircle } from './icons';

interface IntroductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendedUserId: string;
  recommendedUserName: string;
  recommendedUserPhoto?: string;
}

const IntroductionModal: React.FC<IntroductionModalProps> = ({
  isOpen,
  onClose,
  recommendedUserId,
  recommendedUserName,
  recommendedUserPhoto
}) => {
  const [user] = useAuthState(auth);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [message, setMessage] = useState('');
  const [userConnections, setUserConnections] = useState<any[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user?.uid) {
      loadUserConnections();
      setSelectedUser(null);
      setMessage('');
      setSent(false);
      setError(null);
    }
  }, [isOpen, user?.uid]);

  const loadUserConnections = async () => {
    if (!user?.uid) return;
    
    try {
      const connectionsQuery = query(
        collection(db, 'connections'),
        where('user1Id', '==', user.uid)
      );
      const snapshot = await getDocs(connectionsQuery);
      const connections = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Also get connections where user is user2
      const connectionsQuery2 = query(
        collection(db, 'connections'),
        where('user2Id', '==', user.uid)
      );
      const snapshot2 = await getDocs(connectionsQuery2);
      const connections2 = snapshot2.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUserConnections([...connections, ...connections2]);
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const handleSendIntroduction = async () => {
    if (!user?.uid || !selectedUser || !user.displayName) return;
    
    if (selectedUser.userId === recommendedUserId) {
      setError('Je kunt iemand niet aan zichzelf voorstellen');
      return;
    }
    
    setSending(true);
    setError(null);
    try {
      await introductionService.createIntroduction(
        user.uid,
        user.displayName,
        selectedUser.userId,
        recommendedUserId,
        recommendedUserName,
        recommendedUserPhoto,
        message || undefined
      );
      setSent(true);
      setTimeout(() => {
        onClose();
        setSent(false);
        setSelectedUser(null);
        setMessage('');
      }, 2000);
    } catch (error: any) {
      console.error('Error sending introduction:', error);
      setError(error.message || 'Er ging iets mis bij het versturen van de introductie.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">
            Stel {recommendedUserName} voor
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <Icon icon={X} size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Recommended User Info */}
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            {recommendedUserPhoto ? (
              <img
                src={recommendedUserPhoto}
                alt={recommendedUserName}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xl">
                  {recommendedUserName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{recommendedUserName}</h3>
              <p className="text-sm text-gray-600">Wordt voorgesteld aan:</p>
            </div>
          </div>

          {sent ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon icon={CheckCircle} size={32} className="text-green-600" />
              </div>
              <p className="text-lg font-medium text-gray-900">Introductie verstuurd!</p>
              <p className="text-sm text-gray-600 mt-2">
                {selectedUser?.displayName} heeft een notificatie ontvangen.
              </p>
            </div>
          ) : (
            <>
              {/* Search for person to introduce to */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aan wie wil je {recommendedUserName} voorstellen?
                </label>
                <UserSearchBar
                  onSelectUser={(user) => {
                    setSelectedUser(user);
                    setError(null);
                  }}
                  placeholder="Zoek op naam, bedrijf of email..."
                  showResults={true}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {selectedUser && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    {selectedUser.photoURL ? (
                      <img
                        src={selectedUser.photoURL}
                        alt={selectedUser.displayName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {selectedUser.displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{selectedUser.displayName}</p>
                      {selectedUser.companyName && (
                        <p className="text-sm text-gray-600">{selectedUser.companyName}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Optional message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optioneel bericht
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Waarom stel je ${recommendedUserName} voor aan ${selectedUser?.displayName || 'deze persoon'}?`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={4}
                />
              </div>

              {/* Send button */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSendIntroduction}
                  disabled={!selectedUser || sending}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Icon icon={Send} size={18} />
                  {sending ? 'Verzenden...' : 'Verstuur introductie'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntroductionModal;


